import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

/* ─── HOOKS ──────────────────────────────────────────────────── */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const useCounter = (target, run, duration = 2000) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let start = null;
    const step = ts => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(pct * target));
      if (pct < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [run, target, duration]);
  return val;
};

/* ─── ANIMATED COUNTER ──────────────────────────────────────── */
const Counter = ({ target, suffix = '', label }) => {
  const [ref, inView] = useInView(0.2);
  const val = useCounter(target, inView);
  return (
    <div ref={ref} className="stat-item" style={{ textAlign: 'center' }}>
      <span className="stat-number">{val.toLocaleString()}{suffix}</span>
      <div className="stat-label">{label}</div>
    </div>
  );
};

/* ─── REVEAL WRAPPER ─────────────────────────────────────────── */
const Reveal = ({ children, delay = 0, style = {}, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={`reveal${inView ? ' visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}>
      {children}
    </div>
  );
};

/* ─── FAQ ITEM ───────────────────────────────────────────────── */
const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={() => setOpen(o => !o)} style={{
      border: `2px solid ${open ? 'rgba(26,60,143,0.22)' : 'rgba(26,60,143,0.1)'}`,
      borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
      background: open ? 'rgba(26,60,143,0.025)' : '#fff',
      transition: 'all 0.25s', marginBottom: 10
    }}>
      <div style={{ padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A2E' }}>{q}</span>
        <span style={{ fontSize: 22, color: '#1A3C8F', flexShrink: 0, transition: 'transform 0.25s', transform: open ? 'rotate(45deg)' : 'none', lineHeight: 1 }}>+</span>
      </div>
      {open && (
        <div style={{ padding: '0 22px 18px' }}>
          <p style={{ color: '#888', fontSize: 14, lineHeight: 1.7 }}>{a}</p>
        </div>
      )}
    </div>
  );
};

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const particleRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Generate particles */
  useEffect(() => {
    const container = particleRef.current;
    if (!container) return;
    container.innerHTML = '';
    for (let i = 0; i < 20; i++) {
      const p = document.createElement('div');
      p.className = 'particle';
      const size = Math.random() * 8 + 4;
      p.style.cssText = `width:${size}px;height:${size}px;left:${Math.random()*100}%;animation-duration:${Math.random()*15+10}s;animation-delay:${Math.random()*10}s;`;
      container.appendChild(p);
    }
  }, []);

  /* Hero counter trigger on load */
  const [heroRun, setHeroRun] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroRun(true), 800); return () => clearTimeout(t); }, []);

  const heroStudents = useCounter(2500, heroRun);
  const heroQs      = useCounter(15000, heroRun);
  const heroTests   = useCounter(240, heroRun);
  const heroGroups  = useCounter(4, heroRun);

  const smoothScroll = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <div style={{ fontFamily: "'Sora', sans-serif", background: '#F8F9FF', color: '#1A1A2E', overflowX: 'hidden' }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        padding: '0 5%', height: 72,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? 'rgba(26,60,143,0.97)' : 'rgba(248,249,255,0.85)',
        backdropFilter: 'blur(20px)',
        borderBottom: scrolled ? 'none' : '1px solid rgba(26,60,143,0.12)',
        transition: 'all 0.3s ease',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#1A3C8F,#2451b3)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, boxShadow: '0 4px 15px rgba(26,60,143,0.3)' }}>🏆</div>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: scrolled ? '#fff' : '#1A3C8F', transition: 'color 0.3s' }}>
            TNPSC <em style={{ color: '#F5A623', fontStyle: 'normal' }}>Hub</em>
          </span>
        </Link>

        <ul style={{ display: 'flex', gap: 32, listStyle: 'none', margin: 0 }} className="nav-links-home">
          {[['features','Features'],['exams','Exam Groups'],['pricing','Pricing'],['how','How It Works'],['contact','Contact']].map(([id, label]) => (
            <li key={id}><a href={`#${id}`} onClick={e => smoothScroll(e, id)} style={{ color: scrolled ? 'rgba(255,255,255,0.85)' : '#2d3561', fontSize: 14, fontWeight: 500, textDecoration: 'none', transition: 'color 0.3s' }}
              onMouseEnter={e => e.target.style.color = '#F5A623'}
              onMouseLeave={e => e.target.style.color = scrolled ? 'rgba(255,255,255,0.85)' : '#2d3561'}>
              {label}
            </a></li>
          ))}
        </ul>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/login" style={{ padding: '9px 22px', border: `2px solid ${scrolled ? '#F5A623' : '#1A3C8F'}`, borderRadius: 8, background: 'transparent', color: scrolled ? '#F5A623' : '#1A3C8F', fontSize: 14, fontWeight: 600, textDecoration: 'none', transition: 'all 0.3s' }}
            onMouseEnter={e => { e.currentTarget.style.background = scrolled ? '#F5A623' : '#1A3C8F'; e.currentTarget.style.color = scrolled ? '#102766' : '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = scrolled ? '#F5A623' : '#1A3C8F'; }}>
            Login
          </Link>
          <Link to="/register" style={{ padding: '9px 22px', background: 'linear-gradient(135deg,#F5A623,#e8940f)', border: 'none', borderRadius: 8, color: '#102766', fontSize: 14, fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 15px rgba(245,166,35,0.35)', transition: 'all 0.3s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(245,166,35,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(245,166,35,0.35)'; }}>
            Register Now
          </Link>
          {/* Hamburger */}
          <div onClick={() => setMobileOpen(o => !o)} className="hamburger-home" style={{ display: 'none', flexDirection: 'column', gap: 5, cursor: 'pointer', padding: 4 }}>
            {[0,1,2].map(i => <span key={i} style={{ display: 'block', width: 24, height: 2, background: scrolled ? '#fff' : '#1A3C8F', borderRadius: 2 }} />)}
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ position: 'fixed', top: 72, left: 0, right: 0, background: 'rgba(26,60,143,0.97)', zIndex: 999, padding: '20px 5%', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[['features','Features'],['exams','Exam Groups'],['pricing','Pricing'],['how','How It Works'],['contact','Contact']].map(([id, label]) => (
            <a key={id} href={`#${id}`} onClick={e => smoothScroll(e, id)} style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, fontWeight: 500, textDecoration: 'none' }}>{label}</a>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <Link to="/login" onClick={() => setMobileOpen(false)} style={{ flex: 1, textAlign: 'center', padding: '10px', border: '2px solid #F5A623', borderRadius: 8, color: '#F5A623', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>Login</Link>
            <Link to="/register" onClick={() => setMobileOpen(false)} style={{ flex: 1, textAlign: 'center', padding: '10px', background: 'linear-gradient(135deg,#F5A623,#e8940f)', borderRadius: 8, color: '#102766', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>Register</Link>
          </div>
        </div>
      )}

      {/* ── HERO ── */}
      <section className="hero-section" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', padding: '120px 5% 80px', background: 'linear-gradient(135deg,#0d2158 0%,#1A3C8F 50%,#2451b3 100%)' }}>
        {/* Mesh */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', width: 600, height: 600, background: 'radial-gradient(circle,rgba(245,166,35,0.2),transparent 70%)', top: -100, right: -100, animation: 'meshFloat1 8s ease-in-out infinite' }} />
          <div style={{ position: 'absolute', width: 400, height: 400, background: 'radial-gradient(circle,rgba(100,150,255,0.15),transparent 70%)', bottom: -50, left: '10%', animation: 'meshFloat2 10s ease-in-out infinite' }} />
        </div>
        {/* Particles */}
        <div ref={particleRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }} />

        {/* Content */}
        <div style={{ maxWidth: 650, zIndex: 1, animation: 'heroFadeIn 1s ease forwards' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(245,166,35,0.15)', border: '1px solid rgba(245,166,35,0.4)', borderRadius: 50, padding: '8px 18px', marginBottom: 28, animation: 'badgePulse 3s ease-in-out infinite' }}>
            <div style={{ width: 8, height: 8, background: '#F5A623', borderRadius: '50%', animation: 'dotBlink 1.5s ease-in-out infinite' }} />
            <span style={{ color: '#F5A623', fontSize: 13, fontWeight: 600, letterSpacing: '0.5px' }}>Tamil Nadu's #1 Exam Platform</span>
          </div>

          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(42px,6vw,72px)', fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 24 }}>
            Crack Your <span style={{ color: '#F5A623', position: 'relative', display: 'inline-block' }}>TNPSC Exam<span style={{ position: 'absolute', bottom: 2, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#F5A623,transparent)', borderRadius: 2 }} /></span> With Confidence
          </h1>

          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 18, lineHeight: 1.8, marginBottom: 40, fontWeight: 300 }}>
            15,000+ practice questions, weekly mock tests, and real exam-pattern preparation designed to help you secure your dream government job.
          </p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 60 }}>
            <Link to="/register" style={{ padding: '16px 36px', background: 'linear-gradient(135deg,#F5A623,#e8940f)', border: 'none', borderRadius: 12, color: '#102766', fontSize: 16, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 30px rgba(245,166,35,0.4)', transition: 'all 0.3s', position: 'relative', overflow: 'hidden' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(245,166,35,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(245,166,35,0.4)'; }}>
              🚀 Start Preparing — ₹499
            </Link>
            <a href="#exams" onClick={e => smoothScroll(e, 'exams')} style={{ padding: '16px 36px', background: 'transparent', border: '2px solid rgba(255,255,255,0.4)', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, backdropFilter: 'blur(10px)', transition: 'all 0.3s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              📘 View Exam Groups
            </a>
          </div>

          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            {[[heroStudents, 'Students Enrolled'],[heroQs,'Practice Questions'],[heroTests,'Mock Tests'],[heroGroups,'Exam Groups']].map(([n, l]) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#F5A623', display: 'block', lineHeight: 1 }}>{n.toLocaleString()}</span>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating exam cards */}
        <div style={{ position: 'absolute', right: '5%', top: '50%', transform: 'translateY(-50%)', width: 420, zIndex: 1 }} className="hero-visual-home">
          <div style={{ position: 'relative', height: 480 }}>
            {[
              { cls: 'card-1', icon: '🏆', title: 'Group 1', sub: 'Deputy Collector & IAS level', badge: 'Advanced Level', style: { width: 220, top: 0, right: 0, animation: 'cardFloat1 6s ease-in-out infinite' } },
              { cls: 'card-2', icon: '📘', title: 'Group 2', sub: 'Sub Registrar & HR Officer', badge: 'Intermediate', style: { width: 200, top: 120, left: 0, animation: 'cardFloat2 7s ease-in-out infinite' } },
              { cls: 'card-3', icon: '📗', title: 'Group 2A', sub: 'Junior Assistant & Typist', badge: 'Entry Level', style: { width: 240, bottom: 80, right: 20, animation: 'cardFloat3 5s ease-in-out infinite' } },
              { cls: 'card-4', icon: '📙', title: 'Group 4', sub: 'Bill Collector & Jr. Asst.', badge: 'Foundation', style: { width: 180, bottom: 0, left: 20, animation: 'cardFloat4 8s ease-in-out infinite' } },
            ].map(c => (
              <div key={c.title} style={{ position: 'absolute', background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: 24, transition: 'all 0.3s', ...c.style }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.13)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{c.icon}</div>
                <div style={{ color: '#fff', fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>{c.sub}</div>
                <span style={{ display: 'inline-block', marginTop: 10, padding: '4px 10px', background: 'rgba(245,166,35,0.2)', border: '1px solid rgba(245,166,35,0.4)', borderRadius: 20, color: '#F5A623', fontSize: 11, fontWeight: 600 }}>{c.badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WAVE ── */}
      <div style={{ lineHeight: 0, overflow: 'hidden', background: 'linear-gradient(135deg,#0d2158,#2451b3)' }}>
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%' }}>
          <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#F8F9FF"/>
        </svg>
      </div>

      {/* ── STATS ── */}
      <section style={{ padding: '80px 5%', background: 'linear-gradient(135deg,#102766,#1A3C8F)', position: 'relative', overflow: 'hidden' }} id="stats">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")", pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 40, position: 'relative' }} className="stats-grid-home">
          <Counter target={2500} suffix="+" label="Students Registered" />
          <Counter target={15000} suffix="+" label="Practice Questions" />
          <Counter target={240} suffix="+" label="Mock Tests Available" />
          <Counter target={4} suffix="" label="Exam Groups Covered" />
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ padding: '100px 5%', background: '#F8F9FF' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(26,60,143,0.08)', border: '1px solid rgba(26,60,143,0.15)', borderRadius: 50, padding: '6px 16px', marginBottom: 16, color: '#1A3C8F', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>✨ Platform Features</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, color: '#1A1A2E', lineHeight: 1.2, marginBottom: 16 }}>Everything You Need to <span style={{ color: '#1A3C8F' }}>Succeed</span></h2>
            <p style={{ color: '#666', fontSize: 17, maxWidth: 560, margin: '0 auto' }}>A complete ecosystem for TNPSC exam preparation with powerful tools and real exam-level practice.</p>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 28 }}>
            {[
              ['📚','15,000+ Questions','Carefully curated questions covering all subjects — History, Polity, Geography, Science, Tamil, English and more.'],
              ['⏱️','Timed Mock Tests','Real exam conditions with auto-submit timer, question palette, and mark-for-review functionality.'],
              ['📅','Weekly Unlock System','Tests unlock progressively week by week — keeping you structured, motivated and exam-ready.'],
              ['🎯','Daily Quiz','10 fresh questions every single day to keep your knowledge sharp and track consistent progress.'],
              ['📊','Performance Analytics','Detailed subject-wise reports, score trends, weak topic identification and improvement suggestions.'],
              ['📱','Mobile Friendly','Fully responsive design — practice anywhere, anytime on your phone, tablet or computer.'],
            ].map(([icon, title, desc], i) => (
              <Reveal key={title} delay={i * 80}>
                <div style={{ background: '#fff', borderRadius: 20, padding: 36, border: '1px solid rgba(26,60,143,0.1)', transition: 'all 0.4s ease', cursor: 'default', position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(26,60,143,0.15)'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.querySelector('.ficon').style.background = 'linear-gradient(135deg,#1A3C8F,#2451b3)'; e.currentTarget.querySelector('.fbar').style.transform = 'scaleX(1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(26,60,143,0.1)'; e.currentTarget.querySelector('.ficon').style.background = 'linear-gradient(135deg,rgba(26,60,143,0.1),rgba(26,60,143,0.05))'; e.currentTarget.querySelector('.fbar').style.transform = 'scaleX(0)'; }}>
                  <div className="fbar" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#1A3C8F,#F5A623)', transform: 'scaleX(0)', transformOrigin: 'left', transition: 'transform 0.4s ease' }} />
                  <div className="ficon" style={{ width: 60, height: 60, background: 'linear-gradient(135deg,rgba(26,60,143,0.1),rgba(26,60,143,0.05))', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, marginBottom: 20, transition: 'all 0.3s' }}>{icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1A1A2E', marginBottom: 10 }}>{title}</h3>
                  <p style={{ color: '#777', fontSize: 14, lineHeight: 1.7 }}>{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── EXAM GROUPS ── */}
      <section id="exams" style={{ padding: '100px 5%', background: 'linear-gradient(180deg,#f0f4ff,#F8F9FF)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(26,60,143,0.08)', border: '1px solid rgba(26,60,143,0.15)', borderRadius: 50, padding: '6px 16px', marginBottom: 16, color: '#1A3C8F', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>📋 Exam Groups</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, color: '#1A1A2E', lineHeight: 1.2, marginBottom: 16 }}>Choose Your <span style={{ color: '#1A3C8F' }}>Exam Group</span></h2>
            <p style={{ color: '#666', fontSize: 17, maxWidth: 560, margin: '0 auto' }}>Dedicated question banks and mock tests for each TNPSC exam group — tailored to your target post.</p>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 24 }}>
            {[
              { icon: '🏆', name: 'Group 1', posts: 'Deputy Collector, DSP, Assistant Director and other Class I posts', qs: '2550', badge: '⭐⭐⭐⭐⭐ Advanced' },
              { icon: '📘', name: 'Group 2', posts: 'Sub Registrar, HR Officer, Municipal Commissioner and more', qs: '2450', badge: '⭐⭐⭐⭐ Intermediate' },
              { icon: '📗', name: 'Group 2A', posts: 'Junior Assistant, Typist, Steno Typist and entry-level Class II posts', qs: '2450', badge: '⭐⭐⭐ Entry Level' },
              { icon: '📙', name: 'Group 4', posts: 'Bill Collector, Junior Assistant, Field Assistant and more', qs: '2250', badge: '⭐⭐ Foundation' },
            ].map((g, i) => (
              <Reveal key={g.name} delay={i * 100}>
                <div style={{ background: '#fff', borderRadius: 24, padding: '40px 32px', border: '2px solid transparent', textAlign: 'center', cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.175,0.885,0.32,1.275)', position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-10px) scale(1.02)';
                    e.currentTarget.querySelector('.goverlay').style.opacity = '1';
                    Array.from(e.currentTarget.querySelectorAll('.gtext')).forEach(el => el.style.color = '#fff');
                    e.currentTarget.querySelector('.gicon').style.background = 'rgba(255,255,255,0.15)';
                    e.currentTarget.querySelector('.gbadge').style.background = 'rgba(245,166,35,0.3)';
                    e.currentTarget.querySelector('.gbadge').style.color = '#FFD07A';
                    Array.from(e.currentTarget.querySelectorAll('.gstat-num')).forEach(el => el.style.color = '#F5A623');
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.querySelector('.goverlay').style.opacity = '0';
                    Array.from(e.currentTarget.querySelectorAll('.gtext')).forEach(el => el.style.color = '');
                    e.currentTarget.querySelector('.gicon').style.background = 'linear-gradient(135deg,rgba(26,60,143,0.08),rgba(26,60,143,0.04))';
                    e.currentTarget.querySelector('.gbadge').style.background = 'rgba(26,60,143,0.08)';
                    e.currentTarget.querySelector('.gbadge').style.color = '#1A3C8F';
                    Array.from(e.currentTarget.querySelectorAll('.gstat-num')).forEach(el => el.style.color = '#1A3C8F');
                  }}>
                  <div className="goverlay" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#1A3C8F,#2451b3)', opacity: 0, transition: 'opacity 0.4s', zIndex: 0 }} />
                  <div className="gicon" style={{ width: 80, height: 80, margin: '0 auto 24px', background: 'linear-gradient(135deg,rgba(26,60,143,0.08),rgba(26,60,143,0.04))', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, transition: 'all 0.3s', position: 'relative', zIndex: 1 }}>{g.icon}</div>
                  <h3 className="gtext" style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: '#1A3C8F', marginBottom: 8, transition: 'color 0.3s', position: 'relative', zIndex: 1 }}>{g.name}</h3>
                  <p className="gtext" style={{ color: '#888', fontSize: 13, marginBottom: 20, lineHeight: 1.6, transition: 'color 0.3s', position: 'relative', zIndex: 1 }}>{g.posts}</p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 20, position: 'relative', zIndex: 1 }}>
                    {[[g.qs,'Questions'],['60','Tests']].map(([v, l]) => (
                      <div key={l} style={{ textAlign: 'center' }}>
                        <strong className="gstat-num" style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 18, color: '#1A3C8F', transition: 'color 0.3s' }}>{v}</strong>
                        <span className="gtext" style={{ fontSize: 11, color: '#666', opacity: 0.8, transition: 'color 0.3s' }}>{l}</span>
                      </div>
                    ))}
                  </div>
                  <span className="gbadge" style={{ display: 'inline-block', padding: '6px 16px', background: 'rgba(26,60,143,0.08)', border: '1px solid rgba(26,60,143,0.15)', borderRadius: 20, color: '#1A3C8F', fontSize: 12, fontWeight: 600, transition: 'all 0.3s', position: 'relative', zIndex: 1 }}>{g.badge}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding: '100px 5%', background: '#F8F9FF' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(26,60,143,0.08)', border: '1px solid rgba(26,60,143,0.15)', borderRadius: 50, padding: '6px 16px', marginBottom: 16, color: '#1A3C8F', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>🔄 Simple Process</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, color: '#1A1A2E', lineHeight: 1.2, marginBottom: 16 }}>How It <span style={{ color: '#1A3C8F' }}>Works</span></h2>
            <p style={{ color: '#666', fontSize: 17, maxWidth: 480, margin: '0 auto' }}>Get started in minutes and begin your journey to a government job.</p>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 0, position: 'relative' }} className="steps-grid-home">
            <div style={{ position: 'absolute', top: 40, left: '10%', right: '10%', height: 2, background: 'linear-gradient(90deg,#1A3C8F,#F5A623)', zIndex: 0 }} className="steps-line-home" />
            {[
              ['1','✍️','Register','Fill your personal details and create your account'],
              ['2','💳','Pay ₹499','Make payment via UPI and enter your UTR ID'],
              ['3','✅','Get Approved','Admin verifies your payment within 24 hours'],
              ['4','🚀','Start Preparing','Access your dashboard and begin mock tests'],
              ['5','📊','Track Progress','Monitor your performance and improve daily'],
            ].map(([n, icon, title, desc], i) => (
              <Reveal key={n} delay={i * 100} style={{ textAlign: 'center', padding: '0 20px', position: 'relative', zIndex: 1 }}>
                <div style={{ width: 80, height: 80, background: 'linear-gradient(135deg,#1A3C8F,#2451b3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 900, color: '#fff', boxShadow: '0 8px 30px rgba(26,60,143,0.3)', border: '4px solid #F8F9FF', transition: 'all 0.3s', cursor: 'default' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg,#F5A623,#e8940f)'; e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 12px 35px rgba(245,166,35,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg,#1A3C8F,#2451b3)'; e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(26,60,143,0.3)'; }}>
                  {n}
                </div>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: '#1A1A2E', marginBottom: 8 }}>{title}</h4>
                <p style={{ color: '#888', fontSize: 13, lineHeight: 1.6 }}>{desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ padding: '100px 5%', background: '#F8F9FF' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(26,60,143,0.08)', border: '1px solid rgba(26,60,143,0.15)', borderRadius: 50, padding: '6px 16px', marginBottom: 16, color: '#1A3C8F', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>💰 Simple Pricing</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, color: '#1A1A2E', lineHeight: 1.2, marginBottom: 16 }}>One Plan, <span style={{ color: '#1A3C8F' }}>Everything Included</span></h2>
            <p style={{ color: '#666', fontSize: 17, maxWidth: 500, margin: '0 auto' }}>No hidden charges. One affordable plan with complete access to all features.</p>
          </Reveal>

          <Reveal>
            <div style={{ maxWidth: 560, margin: '0 auto', background: 'linear-gradient(135deg,#102766,#1A3C8F)', borderRadius: 32, padding: '56px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: '0 40px 80px rgba(26,60,143,0.35)' }}>
              <div style={{ position: 'absolute', top: '-50%', right: '-20%', width: 400, height: 400, background: 'radial-gradient(circle,rgba(245,166,35,0.15),transparent 70%)', animation: 'pricingGlow 4s ease-in-out infinite', pointerEvents: 'none' }} />

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(245,166,35,0.2)', border: '1px solid rgba(245,166,35,0.5)', borderRadius: 50, padding: '8px 20px', color: '#F5A623', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 28, position: 'relative' }}>
                ⭐ Premium Practice Plan
              </div>

              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#fff', marginBottom: 16, position: 'relative' }}>Complete TNPSC Preparation</h3>

              <div style={{ marginBottom: 40, position: 'relative' }}>
                <span style={{ fontSize: 28, fontWeight: 700, color: '#F5A623', verticalAlign: 'top', lineHeight: 1.5 }}>₹</span>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 80, fontWeight: 900, color: '#fff', lineHeight: 1 }}>499</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, display: 'block', marginTop: 4 }}>One-time payment • Lifetime access</span>
              </div>

              <ul style={{ listStyle: 'none', marginBottom: 40, textAlign: 'left', position: 'relative' }}>
                {[
                  'Access to 15,000+ TNPSC Practice Questions',
                  '10 Full Mock Tests per Group (200 questions each)',
                  '20 Mini Mock Tests per Group',
                  '30 Topic-wise Tests per Group',
                  'Unlimited Daily Quiz',
                  'Detailed Performance Analytics',
                  'Answer Review with Explanations',
                  'Mobile Friendly Platform',
                  'Week-by-week Test Unlock System',
                ].map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', color: 'rgba(255,255,255,0.85)', fontSize: 15, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ width: 22, height: 22, background: 'rgba(40,167,69,0.2)', border: '1px solid rgba(40,167,69,0.4)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0, color: '#5dcc73' }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <Link to="/register" style={{ width: '100%', padding: 18, background: 'linear-gradient(135deg,#F5A623,#e8940f)', border: 'none', borderRadius: 14, color: '#102766', fontFamily: "'Sora', sans-serif", fontSize: 17, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'block', boxShadow: '0 8px 30px rgba(245,166,35,0.4)', transition: 'all 0.3s', position: 'relative', overflow: 'hidden' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(245,166,35,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(245,166,35,0.4)'; }}>
                🚀 Register Now & Start Preparing
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '80px 5%', background: 'linear-gradient(180deg,#f0f4ff,#F8F9FF)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(26,60,143,0.08)', border: '1px solid rgba(26,60,143,0.15)', borderRadius: 50, padding: '6px 16px', marginBottom: 16, color: '#1A3C8F', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>❓ FAQ</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px,4vw,40px)', fontWeight: 700, color: '#1A1A2E' }}>Frequently Asked <span style={{ color: '#1A3C8F' }}>Questions</span></h2>
          </Reveal>
          <FAQ q="How long is the subscription valid?" a="Your ₹499 payment gives you lifetime access. Study at your own pace with no time pressure." />
          <FAQ q="Can I access all 4 exam groups with one payment?" a="Your access is for your selected exam group. The common question bank (6,100+ questions) is shared across all groups automatically." />
          <FAQ q="How does the week-by-week unlock work?" a="From your account activation date, new tests unlock every 7 days — Week 1 (basic), Week 2 (intermediate), Week 3 (advanced), Week 4 (Grand Final mock). This builds your mastery progressively." />
          <FAQ q="How long does payment verification take?" a="Admin verifies UPI payments within 24 hours of submission. You'll receive an email confirmation when your account is activated." />
          <FAQ q="What if my payment is not approved in 24 hours?" a="Email support@tnpschub.msdiscoveries.com or call 9443724724. We'll manually verify and activate within 2 hours during working hours." />
          <FAQ q="Is there a refund policy?" a="Payments are non-refundable once the account is activated. Please review all features before registering." />
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section style={{ padding: '80px 5%', background: 'linear-gradient(135deg,#0d2158,#1A3C8F)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 500, height: 500, background: 'radial-gradient(circle,rgba(245,166,35,0.12),transparent 70%)', top: -150, right: -80, animation: 'meshFloat1 9s ease-in-out infinite', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px,4vw,48px)', fontWeight: 900, color: '#fff', marginBottom: 16 }}>Your Government Job<br /><span style={{ color: '#F5A623' }}>Is One Step Away</span></h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, marginBottom: 36 }}>Join thousands of TNPSC aspirants already practicing on TNPSC Hub.</p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" style={{ padding: '16px 40px', background: 'linear-gradient(135deg,#F5A623,#e8940f)', borderRadius: 14, color: '#102766', fontSize: 16, fontWeight: 800, textDecoration: 'none', boxShadow: '0 8px 28px rgba(245,166,35,0.4)', transition: 'all 0.25s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
              🚀 Register for ₹499
            </Link>
            <Link to="/login" style={{ padding: '16px 32px', background: 'rgba(255,255,255,0.08)', border: '2px solid rgba(255,255,255,0.2)', borderRadius: 14, color: '#fff', fontSize: 15, fontWeight: 600, textDecoration: 'none', transition: 'all 0.25s', backdropFilter: 'blur(10px)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
              Already a member? Login →
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contact" style={{ background: '#1A1A2E', padding: '60px 5% 30px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 48, paddingBottom: 40, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 30 }} className="footer-grid-home">
            <div>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 14, display: 'block' }}>TNPSC <em style={{ color: '#F5A623', fontStyle: 'normal' }}>Hub</em></span>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>An independent online practice platform dedicated to helping Tamil Nadu aspirants crack TNPSC exams with confidence through consistent, quality preparation.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <a href="mailto:support@tnpschub.msdiscoveries.com" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, transition: 'color 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#F5A623'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>📧 support@tnpschub.msdiscoveries.com</a>
                <a href="tel:9443724724" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, transition: 'color 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#F5A623'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>📞 9443724724</a>
              </div>
            </div>
            {[
              { title: 'Exam Groups', links: [['register','Group 1'],['register','Group 2'],['register','Group 2A'],['register','Group 4']] },
              { title: 'Platform', links: [['register','Register'],['login','Login'],['#features','Features'],['#pricing','Pricing']] },
              { title: 'Legal', links: [['/terms','Terms & Conditions'],['/privacy','Privacy Policy'],['/refund','No Refund Policy'],['/contact','Contact Us']] },
            ].map(col => (
              <div key={col.title}>
                <h5 style={{ color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 20 }}>{col.title}</h5>
                <ul style={{ listStyle: 'none' }}>
                  {col.links.map(([href, label]) => (
                    <li key={label} style={{ marginBottom: 10 }}>
                      <Link to={href} style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.3s' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#F5A623'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.transform = 'translateX(0)'; }}>
                        → {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>© 2026 TNPSC Hub — msdiscoveries.com. All rights reserved.</p>
            <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 11, maxWidth: 500, textAlign: 'right' }}>⚠️ TNPSC Hub is not affiliated with or endorsed by the Tamil Nadu Public Service Commission. All content is for educational purposes only.</p>
          </div>
        </div>
      </footer>

      {/* ── GLOBAL STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=Playfair+Display:wght@700;900&family=JetBrains+Mono:wght@400;600&display=swap');

        .particle {
          position: absolute;
          border-radius: 50%;
          background: rgba(245,166,35,0.15);
          animation: particleFloat linear infinite;
        }
        @keyframes particleFloat {
          0% { transform: translateY(100vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100px) rotate(720deg); opacity: 0; }
        }
        @keyframes heroFadeIn { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes meshFloat1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-30px,30px) scale(1.1)} }
        @keyframes meshFloat2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(20px,-20px) scale(1.05)} }
        @keyframes cardFloat1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes cardFloat2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(10px)} }
        @keyframes cardFloat3 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes cardFloat4 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(14px)} }
        @keyframes badgePulse { 0%,100%{box-shadow:0 0 0 0 rgba(245,166,35,0.3)} 50%{box-shadow:0 0 0 8px rgba(245,166,35,0)} }
        @keyframes dotBlink { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.7)} }
        @keyframes pricingGlow { 0%,100%{transform:scale(1);opacity:0.8} 50%{transform:scale(1.2);opacity:1} }

        .reveal { opacity:0; transform:translateY(30px); transition:all 0.7s ease; }
        .reveal.visible { opacity:1; transform:translateY(0); }

        .stat-number {
          font-family:'Playfair Display',serif;
          font-size:52px;
          font-weight:900;
          color:#F5A623;
          display:block;
          line-height:1;
          margin-bottom:8px;
        }
        .stat-label {
          color:rgba(255,255,255,0.7);
          font-size:14px;
          letter-spacing:1px;
          text-transform:uppercase;
        }

        @media(max-width:1024px){
          .hero-visual-home{display:none!important}
          .footer-grid-home{grid-template-columns:1fr 1fr!important}
        }
        @media(max-width:768px){
          .nav-links-home{display:none!important}
          .hamburger-home{display:flex!important}
          .stats-grid-home{grid-template-columns:repeat(2,1fr)!important;gap:24px!important}
          .steps-grid-home{grid-template-columns:repeat(2,1fr)!important}
          .steps-line-home{display:none!important}
          .footer-grid-home{grid-template-columns:1fr!important;gap:32px!important}
        }
        @media(max-width:480px){
          .stats-grid-home{grid-template-columns:1fr 1fr!important}
        }
      `}</style>
    </div>
  );
}
